import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { AccountService } from '/src/api/services/account-service';
import { Account, TCompanyId, TTransactionId } from '/src/lib/models';
import { CreateAccountForm, TCreateAccountRequest, TUpdateAccountRequest, UpdateAccountForm } from '/src/lib/types';

export class AccountStore extends BaseStore {
  constructor(rootStore: RootStore) {
    super(rootStore);
  }

  /**** Data Processing ****/

  /**
   * Tabulates line item balances for an account.
   *
   * @param account - The account to tabulate balances for
   * @returns The input account with the `balance` field set to sum of the line items
   */
  calculateBalances = (account: Account) => {
    // Zero out balance
    let balance = 0;

    // Add up line items
    account.activity = account.activity.map((activity) => ({
      ...activity,
      balance: (balance += activity.amount),
    }));

    return account;
  };

  /**** Account CRUD ****/

  /**
   * Fetches a list of Accounts.
   *
   * @param lenderId - ID of the Lender to fetch Accounts for
   * @returns An array of Accounts
   */
  query = async (lenderId: TCompanyId) => {
    const response = await AccountService.query(lenderId);

    return response.map((account) => this.calculateBalances(account));
  };

  /**
   * Fetches a single Account.
   *
   * @param lenderId - ID of the Lender whose Account is being fetched
   * @param transactionId - The associated Transaction
   * @returns An Account object
   */
  get = async (lenderId: TCompanyId, transactionId: TTransactionId) => {
    const response = await AccountService.get(lenderId, transactionId);

    return this.calculateBalances(response);
  };

  /**
   * Creates an Account item.
   *
   * @param lenderId - ID of the Lender to create an Account for
   * @param formValues - A populated CreateAccountForm object
   * @returns The new Account item (with line item balances)
   */
  create = async (lenderId: TCompanyId, formValues: CreateAccountForm) => {
    // Create new Account object
    const request: TCreateAccountRequest = {
      _id: lenderId,
      amount: formValues.amount,
      effectiveDate: formValues.effectiveDate,
    };

    const response = await AccountService.create(lenderId, request);

    return this.calculateBalances(response);
  };

  /**
   * Updates a Ledger item.
   *
   * @param lenderId - ID of the Lender whose Ledger item is being updated
   * @param transactionId - The associated Transaction
   * @param formValues - A populated UpdateAccountForm object
   * @returns The updated Account (with line item balances)
   */
  update = async (lenderId: TCompanyId, transactionId: TTransactionId, formValues: UpdateAccountForm) => {
    // Create new withdrawal/deposit object
    const request: TUpdateAccountRequest = {
      _id: lenderId,
      amount: formValues.amount,
      transactionId,
    };

    const response = await AccountService.update(lenderId, transactionId, request);

    return this.calculateBalances(response);
  };

  /**
   * Removes a Ledger item.
   *
   * @param lenderId - ID of the Lender whose Ledger item is being removed
   * @param transactionId - The associated Transaction
   * @returns The removed Account
   *
   */
  remove = async (lenderId: TCompanyId, transactionId: TTransactionId) => {
    const response = await AccountService.remove(lenderId, transactionId);

    return this.calculateBalances(response);
  };
}
